<template>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-12">
			<label for="login">{{$t('Squeeze.Login.Login')}} *</label>
			<InputText id="login" v-model="value.login" :class="{'p-invalid':v$.login.$invalid && showErrorMessage}" @change="update" :disabled="user.id || user.externalUser" />
			<small v-if="v$.login.$invalid && showErrorMessage && !v$.login.whitespaceValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Login.Login')}) }}
			</small>
			<small v-if="v$.login.whitespaceValidation.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.NoWhitespace') }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="firstName">{{$t('Squeeze.UserManagement.Firstname')}} *</label>
			<InputText id="firstName" v-model="value.firstName" :class="{'p-invalid':v$.firstName.$invalid && showErrorMessage}" :disabled="user.externalUser" @change="update" />
			<small v-if="v$.firstName.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.UserManagement.Firstname')}) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="lastName">{{$t('Squeeze.UserManagement.Lastname')}} *</label>
			<InputText id="lastName" v-model="value.lastName" :class="{'p-invalid':v$.lastName.$invalid && showErrorMessage}" :disabled="user.externalUser" @change="update" />
			<small v-if="v$.lastName.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.UserManagement.Lastname')}) }}
			</small>
		</div>
		<div class="p-field p-col-6" :disabled="user.id">
			<label for="email">{{$t('Squeeze.UserManagement.Email')}} *</label>
			<InputText id="email" v-model="value.email" :class="{'p-invalid':v$.email.$invalid && showErrorMessage}" @change="update" :disabled="!store.state.scopes.sqzUsersManage || user.externalUser" style="text-transform: lowercase;" />
			<small v-if="showErrorMessage" class="p-error">
				<template v-if="v$.email.email.$invalid">
					{{$t('Forms.Val.Email')}}
				</template>
				<template v-else-if="v$.email.$invalid">
					{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.UserManagement.Email')}) }}
				</template>
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="password">{{$t('Squeeze.Login.Password')}} *</label>
			<Password
				id="password"
				v-model="value.password"
				:class="{'p-invalid':v$.password.$invalid && showErrorMessage}"
				@change="update"
				:feedback="false"
				toggleMask
				:disabled="user.id || user.externalUser"
				v-tooltip.bottom="passwordValidationRules"
			/>
			<small v-if="v$.password.$invalid && showErrorMessage && v$.password.passwordLengthValidation && !v$.password.passwordLengthValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Login.Password')}) }} <br/>
			</small>
			<small v-if="v$.password.passwordLengthValidation && v$.password.passwordLengthValidation.$invalid && showErrorMessage && v$.password.minOneLowerAndUpperCaseValidation && !v$.password.minOneLowerAndUpperCaseValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.PasswordLength') }} <br/>
			</small>
			<small v-if="v$.password.minOneLowerAndUpperCaseValidation && v$.password.minOneLowerAndUpperCaseValidation.$invalid && showErrorMessage && v$.password.atLeastOneNumberValidation && !v$.password.atLeastOneNumberValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.MinOneLowerAndUpperCase', { field: $t('Squeeze.Login.Password')}) }} <br/>
			</small>
			<small v-if="v$.password.atLeastOneNumberValidation && v$.password.atLeastOneNumberValidation.$invalid && showErrorMessage && v$.password.atLeastOneSpecialCharacterValidation && !v$.password.atLeastOneSpecialCharacterValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.AtLeastOneNumber', { field: $t('Squeeze.Login.Password')}) }} <br/>
			</small>
			<small v-if="v$.password.atLeastOneSpecialCharacterValidation && v$.password.atLeastOneSpecialCharacterValidation.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.AtLeastOneSpecialCharacter', { field: $t('Squeeze.Login.Password')}) }} <br/>
			</small>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import InputText from "primevue/inputtext";
import Password from 'primevue/password';
import {User} from "@dex/squeeze-client-ts";
import {email, required} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useSqueezeStore} from "@/apps/squeeze/store";
import {FormState} from "@/util/Types";

type UserFormState = FormState<User, "login" | "firstName" | "lastName" | "email" | "password">;

import {
	atLeastOneSpecialCharacterValidation,
	atLeastOneNumberValidation,
	minOneLowerAndUpperCaseValidation,
	passwordLengthValidation,
	whitespaceValidation
} from "@/util/ValidationHelper";

export default defineComponent({
	name: "UserForm",
	methods: {
		atLeastOneSpecialCharacterValidation,
		atLeastOneNumberValidation,
		minOneLowerAndUpperCaseValidation,
		passwordLengthValidation,
		whitespaceValidation,
	},
	components: {
		InputText,
		Password,
	},
	props: {
		userId: {
			type: Number,
			default: 0,
		},
		user: {
			type: Object as PropType<User>,
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();

		/** String with all password validation rules */
		const passwordValidationRules = t('Squeeze.Login.PasswordMustInclude') +
			' \n - ' + t('Forms.Val.PasswordLength') +
			' \n - ' + t('Forms.Val.MinOneLowerAndUpperCase', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneNumber', { field: t('Squeeze.Login.Password')}) +
			' \n - ' + t('Forms.Val.AtLeastOneSpecialCharacter', { field: t('Squeeze.Login.Password')});

		/** Show loading? */
		const loading = ref(false);

		/** Current Object of all input-fields */
		const value = reactive<UserFormState>({
			login: '',
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			externalUser: false,
		});

		/** Determines the required rules for validation */
		let rules = {
			login: { required, whitespaceValidation },
			firstName: { required },
			lastName: { required },
			email: { required, email},
			password: { },
		}

		if (!props.userId) {
			rules = {
				login: { required, whitespaceValidation },
				firstName: { required },
				lastName: { required },
				email: { required, email},
				password: {
					required,
					passwordLengthValidation,
					minOneLowerAndUpperCaseValidation,
					atLeastOneNumberValidation,
					atLeastOneSpecialCharacterValidation,
				},
			}
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.user);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.user, async () => {
			Object.assign(value, props.user);
			await nextTick();
			emit("update", value, v$.value.$invalid);
		})

		/** Triggered on change of any field */
		const update = () => {
			emit("update", value, v$.value.$invalid);
		}

		return {
			t,
			toast,
			store,
			passwordValidationRules,
			loading,
			value,
			props,
			v$,
			update,
		}
	},
});

</script>

<style scoped>

</style>
